import React from "react";
import { Card, Tag, Descriptions, Space, Typography } from "antd";
import { GalleryItem } from "./CardGallery";
import nissanLogo from '../assets/nissan_logo.svg';
import gmLogo from '../assets/gm_logo.svg';
import fordLogo from '../assets/ford_logo.svg';
import hondaLogo from '../assets/honda.png';
import './Outflow.css';

export interface OutflowRowProps extends GalleryItem {
  id: number;
  system_name: string;
  outflow_total: number;
  status: string;
  job_num: number;
  job_start: string;
  job_end: string;
  part_name: string;
  part_number: string;
  model: string;
}

export interface OutflowCardProps {
  item: OutflowRowProps;
  onNavigate: (id: number, system_name?: string) => void;
}

const getLogo = (systemName: string) => {
  switch (true) {
    case systemName.toLowerCase().includes("nissan"):
      return nissanLogo;
    case systemName.toLowerCase().includes("gm"):
      return gmLogo;
    case systemName.toLowerCase().includes("ford"):
      return fordLogo;
    case systemName.toLowerCase().includes("honda"):
      return hondaLogo;
    default:
      return null;
  }
};

const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case "completed":
      return "success";
    case "open":
      return "processing";
    case "pending":
      return "warning";
    case "failed":
      return "error";
    default:
      return "default";
  }
};

export const OutflowCard: React.FC<OutflowCardProps> = ({
  item,
  onNavigate,
}: OutflowCardProps) => {
  const logo = getLogo(item.system_name);
  const statusColor = getStatusColor(item.status);
  
  return (
    <Card
      hoverable
      className="outflow-card overflow-hidden rounded-lg shadow-md transition-all w-full hover:shadow-lg"
      styles={{ body:{padding: "16px", height: "100%" }}}
      onClick={() => onNavigate(item.id, item.system_name)}
      style={{ 
        borderTop: `2px solid transparent`,
        transition: "all 0.3s ease"
      }}
    >
      <div className="flex flex-col h-full">
        {/* Logo Section */}
        <div className="h-32 flex items-center justify-center mb-4 bg-gray-50 rounded p-2">
          {logo ? (
            <img
              src={logo}
              alt={`${item.system_name} logo`}
              className="max-h-full max-w-full object-contain"
            />
          ) : (
            <Typography.Text type="secondary">No Logo</Typography.Text>
          )}
        </div>
        
        {/* Header with System Name and Status */}
        <div className="mb-4 flex justify-between items-center">
          <Typography.Title level={5} className="m-0">
            {item.system_name}
          </Typography.Title>
          <Tag color={statusColor}>{item.status}</Tag>
        </div>
        
        {/* Key Info */}
        <Space direction="vertical" size="small" className="mb-2">
          <div className="flex justify-between">
            <Typography.Text type="secondary">Outflow Total:</Typography.Text>
            <Typography.Text strong>{item.outflow_total}</Typography.Text>
          </div>
          <div className="flex justify-between">
            <Typography.Text type="secondary">Job Number:</Typography.Text>
            <Typography.Text strong>{item.job_num}</Typography.Text>
          </div>
        </Space>
        
        {/* Collapsible Details */}
        <Descriptions
          size="small"
          column={1}
          bordered
          className="mt-2"
          styles={{ label: { fontWeight: "normal" },
                    content: { textAlign: "right"} }}
          >
          <Descriptions.Item label="Part Name">{item.part_name}</Descriptions.Item>
          <Descriptions.Item label="Part Number">{item.part_number}</Descriptions.Item>
          <Descriptions.Item label="Model">{item.model}</Descriptions.Item>
          <Descriptions.Item label="Job Start">
            {new Intl.DateTimeFormat("en-US", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).format(new Date(item.job_start))}
          </Descriptions.Item>
          <Descriptions.Item label="Job End">
            {new Intl.DateTimeFormat("en-US", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).format(new Date(item.job_end))}
          </Descriptions.Item>
        </Descriptions>
      </div>
    </Card>
  );
};